/**
 * The profile screen — «PROFILE PAGE».
 *
 * There are no accounts in this build (removed deliberately; DECISIONS D4), so
 * everything here reads from what the device already knows: chapters finished
 * come out of the save, favourite stories out of the library's own list. The
 * nickname is stored locally and editable.
 *
 * Player level and achievements have no system behind them at all. They are
 * drawn because the board draws them, and they show a real zero rather than an
 * invented number — a fake level is a promise the game has not made.
 *
 * The avatar and the name are not edited in place. Figma gives each its own
 * pop-up — «Выберите аватар» and «Выберите никнейм» — and an inline text field
 * where the board draws a modal is a different screen, not a shortcut to it.
 */
import { useEffect, useState } from 'react';
import type { Catalog } from '@hon/schema';
import { BottomNav } from './BottomNav.js';
import { AvatarPicker, NicknamePicker, FACES, DEFAULT_FACE } from './ProfileDialogs.js';

const NAME_KEY = 'hon.nickname';
const FACE_KEY = 'hon.face';
const FAVS_KEY = 'hon.favs';
const SAVE_KEY = 'hon.save';

const read = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

export function Profile({
  catalog,
  baseUrl,
  lang,
  onBack,
  onLang,
  onShop,
  onSettings,
  tickets,
  coins,
}: {
  catalog: Catalog;
  baseUrl: string;
  lang: 'ru' | 'en';
  onBack: () => void;
  onLang: () => void;
  onShop: () => void;
  onSettings: () => void;
  tickets: number;
  coins: number;
}) {
  const [name, setName] = useState(() => localStorage.getItem(NAME_KEY) ?? 'Мика');
  const [face, setFace] = useState(() => {
    const saved = Number(localStorage.getItem(FACE_KEY));
    return FACES.includes(saved as (typeof FACES)[number]) ? saved : DEFAULT_FACE;
  });
  const [picking, setPicking] = useState(false);
  const [renaming, setRenaming] = useState(false);

  useEffect(() => { localStorage.setItem(NAME_KEY, name); }, [name]);
  useEffect(() => { localStorage.setItem(FACE_KEY, String(face)); }, [face]);

  /* Chapters finished is the one number the device genuinely knows. */
  const chapters = read<{ completed?: string[] }>(SAVE_KEY, {}).completed?.length ?? 0;
  const favIds = read<string[]>(FAVS_KEY, []);
  const favs = catalog.books.filter((b) => favIds.includes(b.id) && b.cover);

  return (
    <div className="profile">
      <div className="profile-wash" aria-hidden="true" />

      <button className="profile-back" onClick={onBack} aria-label="Назад">
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M20 12H5m6-6-6 6 6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {/* The board carries the same wallet bar and footer nav as every other
          screen; without them the profile floats free of the rest of the app. */}
      <header className="profile-top">
        <span className="profile-pill">
          <span className="profile-pill-ico ico-ticket" aria-hidden="true" />
          <span className="profile-pill-num is-ticket">{tickets}</span>
          <span className="profile-pill-plus is-ticket" aria-hidden="true">+</span>
        </span>
        <span className="profile-pill">
          <span className="profile-pill-ico ico-coin" aria-hidden="true" />
          <span className="profile-pill-num is-coin">{coins}</span>
          <span className="profile-pill-plus is-coin" aria-hidden="true">+</span>
        </span>
        <button className="profile-lang" onClick={onLang} aria-label="Switch language">
          <span className="nav-ico ico-globe" />
        </button>
      </header>

      <div className="profile-head">
        <button
          className={`profile-avatar is-face-${face}`}
          onClick={() => setPicking(true)}
          aria-label="Сменить аватар"
        />
        <div className="profile-id">
          <button className="profile-name" onClick={() => setRenaming(true)}>
            <span className="profile-name-text">{name}</span>
            {/* The board sets a pencil and «. . .» to the right of the name. */}
            <span className="profile-pencil" aria-hidden="true" />
            <span className="profile-dots" aria-hidden="true">. . .</span>
          </button>
          <div className="profile-rule" aria-hidden="true" />
          <p className="profile-status">статус: <span className="profile-online">онлайн</span></p>
        </div>
      </div>

      <ul className="profile-stats">
        <li className="profile-stat is-gold">
          <span className="profile-stat-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="m12 2 3 6.5 7 .9-5 4.8 1.2 7L12 17.8 5.8 21.2 7 14.2 2 9.4l7-.9L12 2Z" />
            </svg>
          </span>
          <span className="profile-stat-label">Уровень игрока</span>
          <span className="profile-stat-value">0</span>
        </li>
        <li className="profile-stat">
          <span className="profile-stat-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="M4 4h6a2 2 0 0 1 2 2v14a2 2 0 0 0-2-2H4V4Zm16 0h-6a2 2 0 0 0-2 2v14a2 2 0 0 1 2-2h6V4Z" />
            </svg>
          </span>
          <span className="profile-stat-label">Пройденные главы</span>
          <span className="profile-stat-value">{chapters}</span>
        </li>
        <li className="profile-stat">
          <span className="profile-stat-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="currentColor" d="m12 2 2.4 2.1 3.1-.5 1 3 3 1-.5 3.1L23 16l-2.1 2.4.5 3.1-3 1-1 3-3.1-.5L12 27l-.9-.9-2.4 2.1" opacity="0" />
              <path fill="currentColor" d="m12 2 2.6 1.9 3.2-.2.9 3.1 2.8 1.5-1 3 1 3-2.8 1.5-.9 3.1-3.2-.2L12 21l-2.6-1.9-3.2.2-.9-3.1L2.5 14.7l1-3-1-3L5.3 7.2l.9-3.1 3.2.2L12 2Z" />
            </svg>
          </span>
          <span className="profile-stat-label">Собранные достижения</span>
          <span className="profile-stat-value">0</span>
        </li>
      </ul>

      <h2 className="profile-heading is-stories">ИЗБРАННЫЕ ИСТОРИИ</h2>
      <div className="profile-row is-stories">
        {favs.slice(0, 2).map((b) => (
          <div
            key={b.id}
            className="profile-card"
            style={{ backgroundImage: `url(${baseUrl}/${b.cover})` }}
            role="img"
            aria-label={b.title[lang]}
          />
        ))}
        <button className="profile-add" onClick={onBack} aria-label="Добавить историю">+</button>
      </div>

      <h2 className="profile-heading is-faces">ЛЮБИМЫЕ ПЕРСОНАЖИ</h2>
      <div className="profile-row is-faces">
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className={`profile-face is-face-${n}`} aria-hidden="true" />
        ))}
        <button className="profile-add" aria-label="Добавить персонажа">+</button>
      </div>

      <p className="profile-signout">
        <span className="profile-signout-text">ВЫЙТИ С АККАУНТА</span>
        <span className="profile-signout-ico" aria-hidden="true" />
      </p>

      <BottomNav current="profile" lang={lang} onShop={onShop} onProfile={() => {}} onSettings={onSettings} />

      {picking && (
        <AvatarPicker face={face} onPick={setFace} onClose={() => setPicking(false)} />
      )}
      {renaming && (
        <NicknamePicker name={name} onPick={setName} onClose={() => setRenaming(false)} />
      )}
    </div>
  );
}
